import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Card, Spin, Alert, Button, Dropdown, Menu } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getProfitAndLoss } from "./account.api";
import "./account.css";

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const buildProfitLossRows = (data) => {
  const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
  const rows = [];
  const revenue = data?.revenue || [];
  const expense = data?.expense || [];
  revenue.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), "Revenue"]));
  rows.push(["Total Revenue", fmt(data?.totalRevenue), "Revenue"]);
  expense.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), "Expense"]));
  rows.push(["Total Expense", fmt(data?.totalExpense), "Expense"]);
  rows.push(["Profit / (Loss)", fmt(data?.profit), "Profit"]);
  return rows;
};

const exportPDF = (data) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  doc.setFontSize(12);
  doc.text("Profit and Loss Report", 40, 30);
  const headers = [["Account", "Amount"]];
  const tableRows = buildProfitLossRows(data).map((r) => [r[0], r[1]]);
  doc.autoTable({
    head: headers,
    body: tableRows,
    startY: 40,
    theme: "grid",
    headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 8 },
  });
  doc.save("profit-and-loss.pdf");
};

const exportExcel = (data) => {
  const rows = [["Account", "Amount", "Section"], ...buildProfitLossRows(data)];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Profit and Loss");
  XLSX.writeFile(wb, "profit-and-loss.xlsx");
};

const handlePrint = () => {
  window.print();
};

const ProfitAndLoss = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setError(null);
        const dataFromServer = await getProfitAndLoss();
        setData(dataFromServer);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to fetch profit and loss report");
        console.error("Error loading profit and loss:", err);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  if (loading) {
    return (
      <div className="pl-page">
        <Card className="pl-card">
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "50px" }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pl-page">
        <Card className="pl-card">
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }

  const revenue = data?.revenue || [];
  const expense = data?.expense || [];

  return (
    <div className="pl-page">
      <Card className="pl-card pl-card-modern" bordered={false}>
        <div className="pl-header">
          <h1 className="pl-main-title">Profit and Loss Report</h1>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={() => exportPDF(data)}>
                  Download PDF
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => exportExcel(data)}>
                  Download Excel
                </Menu.Item>
                <Menu.Item key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
                  Print
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" className="pl-export-btn">
              Download / Print <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        <div className="pl-table-wrap">
          <table className="pl-table">
            <thead>
              <tr>
                <th>ACCOUNT</th>
                <th className="pl-th-amount">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr className="pl-section-header pl-section-revenue">
                <td colSpan={2}>REVENUE</td>
              </tr>
              {revenue.map((item, index) => (
                <tr key={`rev-${(item.subAccount || "")}-${index}`}>
                  <td className="pl-account-name">{item.subAccount || "—"}</td>
                  <td className="pl-amount">{formatAmount(item.balance)}</td>
                </tr>
              ))}
              <tr className="pl-total-row">
                <td><strong>Total Revenue</strong></td>
                <td className="pl-amount"><strong>{formatAmount(data?.totalRevenue)}</strong></td>
              </tr>

              <tr className="pl-section-header pl-section-expense">
                <td colSpan={2}>EXPENSE</td>
              </tr>
              {expense.map((item, index) => (
                <tr key={`exp-${(item.subAccount || "")}-${index}`}>
                  <td className="pl-account-name">{item.subAccount || "—"}</td>
                  <td className="pl-amount">{formatAmount(item.balance)}</td>
                </tr>
              ))}
              <tr className="pl-total-row">
                <td><strong>Total Expense</strong></td>
                <td className="pl-amount"><strong>{formatAmount(data?.totalExpense)}</strong></td>
              </tr>

              <tr className="pl-section-header pl-section-profit">
                <td colSpan={2}>PROFIT / (LOSS)</td>
              </tr>
              <tr className="pl-total-row">
                <td><strong>Profit / (Loss)</strong></td>
                <td className="pl-amount"><strong>{formatAmount(data?.profit)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="pl-footer">
          <span className="pl-summary">
            Total Revenue: {formatAmount(data?.totalRevenue)} &nbsp;|&nbsp; Total Expense: {formatAmount(data?.totalExpense)} &nbsp;|&nbsp; Profit / (Loss): {formatAmount(data?.profit)}
          </span>
        </div>
      </Card>
    </div>
  );
};

export default ProfitAndLoss;
